/**
 * Data normalization utilities to handle object OR array API responses
 * Fixes the "E.find is not a function" error by ensuring data is always an array
 */

/**
 * Converts unknown data to array format
 * Handles both object and array inputs safely
 */
export function toArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && data !== null) {
    return Object.values(data);
  }
  return [];
}

/**
 * Safe find function that works with both objects and arrays
 * Always converts to array first, then applies find
 */
export function safeFind(data, predicate) {
  const arrayData = toArray(data);
  return arrayData?.find(predicate);
}

/**
 * Converts object with key-value pairs to array of objects
 * Useful when API returns { persons_all: 123, arrests_all: 456 } 
 * but UI needs [{ key: 'persons_all', value: 123 }, { key: 'arrests_all', value: 456 }]
 */
export function objectToKeyValueArray(obj) {
  if (!obj || typeof obj !== 'object') return [];
  
  return Object.keys(obj)?.map(key => ({
    key: key,
    name: key, // Support both key and name properties
    value: Number(obj?.[key] ?? 0)
  }));
}

/**
 * Safe array access with fallback
 * Returns empty array if data is not an array
 */
export function safeArray(data) {
  return Array.isArray(data) ? data : [];
}

/**
 * Safe object access with fallback
 * Returns empty object if data is not an object
 */
export function safeObject(data) {
  return (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
}

/**
 * Normalize count data for Step-3 monitoring
 * Handles both array and object responses from /api/health/counts
 */
export function normalizeCounts(data) {
  // If already an array of count objects, return as-is
  if (Array.isArray(data) && data?.length > 0 && data?.[0]?.key) {
    return data;
  }
  
  // If it's an object with count properties, convert to array
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return objectToKeyValueArray(data);
  }
  
  // Fallback to empty array
  return [];
}

/**
 * Find count value by key name (supports both key and name properties)
 * Used for Step-3 monitoring to find specific metrics like persons_all
 */
export function findCountValue(data, keyName) {
  const normalizedData = normalizeCounts(data);
  const found = normalizedData?.find(item => 
    item?.key === keyName || item?.name === keyName
  );
  return Number(found?.value ?? 0);
}